import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useQuery } from '@tanstack/react-query';
import type {
  CellEditRequestEvent,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import type { AgGridReactProps } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { WithLocalStorage } from '@/components';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/toast';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef, lookupSideBar } from '@/grid/defaults';
import { getRowId } from '@/grid/types';
import { dateOnlyFormatter } from '@/grid/value-formatters/dateOnlyFormatter';
import { useAccessToken } from '@/services/auth';
import { PrimaryContext } from '@/services/context';
import {
  onFarmImplantPregCheckImplantDatesOptions,
  type OnFarmImplantPregCheckRow,
  onFarmImplantPregCheckWorklistOptions,
  OnFarmImplantRequestError,
  useRecordOnFarmImplantPregChecks,
} from '@/services/on-farm-implant';
import { useContextMenu } from '@/utils/context-menu';

import {
  canRecordPregnancyLoss,
  checkFor,
  parsePregCheckColId,
  PREG_CHECK_COLUMN_NUMBERS,
  PREG_CHECK_EDIT_FIELDS,
  pregCheckColId,
  type PregCheckEditField,
  type PregCheckEditHistory,
  pregCheckHistoryKey,
  usePregCheckColumnDefs,
} from './columnDefs';
import { PregnancyLossDialog } from './PregnancyLossDialog';
import { UpdateResultsDialog } from './UpdateResultsDialog';

export const PREG_CHECK_DATE_PARAM = 'pcDate';

type PregCheckTabProps = {
  sessionId: string | null;
};

const TabMessage = ({
  icon,
  title,
  body,
  children,
}: {
  icon: string;
  title: string;
  body?: string;
  children?: React.ReactNode;
}) => (
  <div className="flex flex-1 flex-col items-center justify-center gap-1 py-24 text-gray-400">
    <i className={`${icon} mb-2 text-4xl`} />
    <p className="text-base font-medium">{title}</p>
    {body && <p className="text-sm">{body}</p>}
    {children}
  </div>
);

export const PregCheckTab = ({ sessionId }: PregCheckTabProps) => {
  const token = useAccessToken();
  const { t } = useTranslation();
  const gridOptions = useDefaultGridOptions();
  const { themeMode } = useContext(PrimaryContext);
  const { getContextMenuItems } = useContextMenu({ idKey: 'embryoId' });
  const { mutateAsync: recordPregChecks } = useRecordOnFarmImplantPregChecks();
  const [editHistory, setEditHistory] = useState<PregCheckEditHistory>(
    () => new Map(),
  );

  const saveCheckEdit = useCallback(
    async (
      row: OnFarmImplantPregCheckRow,
      checkNumber: number,
      field: PregCheckEditField,
      value: number | string,
    ) => {
      const check = checkFor(row, checkNumber);
      const result = field === 'result' ? (value as number) : check?.result;
      if (!sessionId || result == null) return false;
      try {
        await recordPregChecks({
          sessionId,
          input: {
            sessionQueueIds: [row.sessionQueueId],
            checkNumber,
            result,
            ...(field === 'checkDate' && { checkDate: value as string }),
            ...(field === 'checkedById' && { checkedById: value as string }),
            versions: row.versions,
          },
        });
        return true;
      } catch (error) {
        const problems =
          error instanceof OnFarmImplantRequestError ? error.problems : [];
        if (!problems.length) {
          console.error('Failed to save preg check change:', error);
        }
        toast.add({
          data: { variant: 'error' },
          title: t('onFarmImplant.pregCheck.editFailed'),
          ...(problems.length && { description: problems.join(' ') }),
        });
        return false;
      }
    },
    [recordPregChecks, sessionId, t],
  );

  const handleCellEditRequest = useCallback(
    async (event: CellEditRequestEvent) => {
      const row = event.data as OnFarmImplantPregCheckRow | undefined;
      const colId = event.column.getColId();
      const parsed = parsePregCheckColId(colId);
      const next = event.newValue as number | string | null | undefined;
      if (!row || !parsed || next == null || next === event.oldValue) return;

      const saved = await saveCheckEdit(
        row,
        parsed.checkNumber,
        parsed.field,
        next,
      );
      const previous = event.oldValue as number | string | null | undefined;
      if (saved && previous != null) {
        setEditHistory((current) =>
          new Map(current).set(
            pregCheckHistoryKey(row.sessionQueueId, colId),
            previous,
          ),
        );
      }
    },
    [saveCheckEdit],
  );

  const handleRevert = useCallback(
    async (
      row: OnFarmImplantPregCheckRow,
      checkNumber: number,
      field: PregCheckEditField,
      previous: number | string,
    ) => {
      const saved = await saveCheckEdit(row, checkNumber, field, previous);
      if (!saved) return;
      setEditHistory((current) => {
        const next = new Map(current);
        next.delete(
          pregCheckHistoryKey(
            row.sessionQueueId,
            pregCheckColId(checkNumber, field),
          ),
        );
        return next;
      });
    },
    [saveCheckEdit],
  );

  const columnDefs = usePregCheckColumnDefs({
    history: editHistory,
    onRevert: handleRevert,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchText, setSearchText] = useState('');
  const sideBar = useMemo(() => {
    const base = lookupSideBar('on-farm-implant-preg-check');
    return {
      ...base,
      toolPanels: base.toolPanels?.map((panel) =>
        typeof panel === 'string' || panel.id !== 'customViews'
          ? panel
          : {
              ...panel,
              toolPanelParams: {
                ...panel.toolPanelParams,
                onClearAllFilters: () => setSearchText(''),
              },
            },
      ),
    };
  }, []);
  const gridRef = useRef<AgGridReact>(null);

  useEffect(() => {
    gridRef.current?.api?.refreshCells({
      columns: PREG_CHECK_COLUMN_NUMBERS.flatMap((n) =>
        PREG_CHECK_EDIT_FIELDS.map((field) => pregCheckColId(n, field)),
      ),
      force: true,
    });
  }, [editHistory]);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [lossOpen, setLossOpen] = useState(false);

  const implantDate = searchParams.get(PREG_CHECK_DATE_PARAM) ?? '';

  const handleSelectionChanged = useCallback((event: SelectionChangedEvent) => {
    setSelectedIds(
      (event.api.getSelectedRows() as OnFarmImplantPregCheckRow[]).map(
        (row) => row.sessionQueueId,
      ),
    );
  }, []);

  const handleClearSelection = useCallback(() => {
    gridRef.current?.api.deselectAll();
  }, []);

  const datesQuery = useQuery({
    ...onFarmImplantPregCheckImplantDatesOptions(token, {
      sessionId: sessionId ?? '',
    }),
    enabled: !!token && !!sessionId,
  });

  const worklistQuery = useQuery({
    ...onFarmImplantPregCheckWorklistOptions(token, {
      sessionId: sessionId ?? '',
      implantDate,
    }),
    enabled: !!token && !!sessionId && !!implantDate,
  });

  const dateOptions = useMemo(
    () =>
      (datesQuery.data ?? []).map((option) => ({
        value: option.implantDate.slice(0, 10),
        label: t('onFarmImplant.pregCheck.dateOption', {
          date: dateOnlyFormatter({ value: option.implantDate }),
          count: option.recipientCount,
        }),
      })),
    [datesQuery.data, t],
  );

  const setImplantDate = (value: string | null) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) {
          next.set(PREG_CHECK_DATE_PARAM, value);
        } else {
          next.delete(PREG_CHECK_DATE_PARAM);
        }
        return next;
      },
      { replace: true },
    );
  };

  if (!sessionId) {
    return (
      <TabMessage
        icon="pi pi-heart"
        title={t('onFarmImplant.pregCheck.noSession')}
      />
    );
  }

  const rows = worklistQuery.data?.items ?? [];
  const selectedIdSet = new Set(selectedIds);
  const selectedRows = rows.filter((row) =>
    selectedIdSet.has(row.sessionQueueId),
  );
  const canRecordLoss =
    selectedRows.length > 0 && selectedRows.every(canRecordPregnancyLoss);

  const renderBody = () => {
    if (!implantDate) {
      return (
        <TabMessage
          icon="pi pi-calendar"
          title={t('onFarmImplant.pregCheck.selectDateTitle')}
          body={t('onFarmImplant.pregCheck.selectDateBody')}
        />
      );
    }

    if (worklistQuery.isLoading) {
      return (
        <div className="flex flex-1 items-center justify-center gap-2 py-24 text-sm text-gray-500">
          <i className="pi pi-spin pi-spinner text-sm" />
          {t('onFarmImplant.pregCheck.loading')}
        </div>
      );
    }

    if (worklistQuery.isError) {
      return (
        <TabMessage
          icon="pi pi-exclamation-triangle"
          title={t('onFarmImplant.pregCheck.loadError')}
        >
          <Button
            className="mt-3"
            onClick={() => {
              void worklistQuery.refetch();
            }}
          >
            {t('onFarmImplant.pregCheck.retry')}
          </Button>
        </TabMessage>
      );
    }

    if (rows.length === 0) {
      return (
        <TabMessage
          icon="pi pi-heart"
          title={t('onFarmImplant.pregCheck.emptyTitle')}
          body={t('onFarmImplant.pregCheck.emptyBody')}
        />
      );
    }

    return (
      <>
        <div className="mb-2 flex items-center gap-3">
          <div className="relative w-56">
            <i className="pi pi-search absolute top-1/2 left-2 -translate-y-1/2 text-xs text-gray-400" />
            <Input
              placeholder={t('onFarmImplant.pregCheck.filterPlaceholder')}
              className="h-7 pl-7 text-xs"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
            />
          </div>
          <span className="text-xs text-gray-500">
            {t('onFarmImplant.pregCheck.recipientCount', {
              count: worklistQuery.data?.totalRecords ?? rows.length,
            })}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-gray-500">
              {t('onFarmImplant.pregCheck.selectedCount', {
                count: selectedIds.length,
              })}
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={selectedIds.length === 0}
              onClick={handleClearSelection}
            >
              {t('onFarmImplant.pregCheck.clearSelection')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!canRecordLoss}
              title={t('onFarmImplant.pregCheck.loss.buttonHint')}
              onClick={() => setLossOpen(true)}
            >
              {t('onFarmImplant.pregCheck.loss.button')}
            </Button>
            <Button
              size="sm"
              disabled={selectedRows.length === 0}
              onClick={() => setUpdateOpen(true)}
            >
              {t('onFarmImplant.pregCheck.update.button')}
            </Button>
          </div>
        </div>

        <div
          className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} h-[calc(100vh-16rem)] min-h-[400px] w-full`}
        >
          <WithLocalStorage storageKey="on-farm-implant-preg-check-worklist-grid">
            <AgGridReact
              ref={gridRef}
              gridOptions={gridOptions}
              columnDefs={columnDefs as AgGridReactProps['columnDefs']}
              defaultColDef={defaultColDef}
              rowData={rows}
              headerHeight={36}
              rowHeight={40}
              quickFilterText={searchText}
              getRowId={getRowId<OnFarmImplantPregCheckRow>(
                (params) => params.data.sessionQueueId,
              )}
              getContextMenuItems={getContextMenuItems}
              sideBar={sideBar}
              rowSelection="multiple"
              suppressRowClickSelection
              onSelectionChanged={handleSelectionChanged}
              readOnlyEdit
              onCellEditRequest={handleCellEditRequest}
            />
          </WithLocalStorage>
        </div>

        <UpdateResultsDialog
          open={updateOpen}
          onOpenChange={setUpdateOpen}
          sessionId={sessionId}
          rows={selectedRows}
          onSaved={handleClearSelection}
        />
        <PregnancyLossDialog
          open={lossOpen}
          onOpenChange={setLossOpen}
          sessionId={sessionId}
          rows={selectedRows}
          onSaved={handleClearSelection}
        />
      </>
    );
  };

  return (
    <div className="flex flex-1 flex-col px-4 py-3">
      <div className="mb-3 flex items-end gap-3">
        <div className="flex flex-col gap-1">
          <Label htmlFor="preg-check-implant-date" className="text-xs">
            {t('onFarmImplant.pregCheck.implantDate')}
          </Label>
          <Select
            items={dateOptions}
            value={implantDate || null}
            onChange={(value) =>
              setImplantDate((value as string | null) ?? null)
            }
            disabled={datesQuery.isLoading || dateOptions.length === 0}
          >
            <SelectTrigger
              id="preg-check-implant-date"
              className="h-8 w-64 text-xs"
            >
              <SelectValue
                placeholder={
                  datesQuery.isLoading
                    ? t('onFarmImplant.pregCheck.loadingDates')
                    : datesQuery.isError
                      ? t('onFarmImplant.pregCheck.loadDatesError')
                      : dateOptions.length === 0
                        ? t('onFarmImplant.pregCheck.noDates')
                        : t('onFarmImplant.pregCheck.selectDate')
                }
              />
            </SelectTrigger>
            <SelectContent>
              {dateOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {datesQuery.isError && (
          <Button
            variant="outline"
            className="h-8 text-xs"
            onClick={() => {
              void datesQuery.refetch();
            }}
          >
            {t('onFarmImplant.pregCheck.retry')}
          </Button>
        )}
      </div>

      {renderBody()}
    </div>
  );
};
